import {
  LitElement,
  html,
  classMap,
  ifDefined,
  property,
} from '@oskecare/fe-utilities/lib/LitElement/index.js';
import { bound } from '@oskecare/fe-utilities/lib/decorators';
import { debounce } from '@oskecare/fe-utilities/scripts/helpers.js';
import '../message/index.js';
import style from './style.scss';

/**
 * Custom OSK input component with label
 *
 * Slots:
 * - label - Label can be provided by attribute (string only) or by slot `label`
 * - prependInput - content will be prepended to input element
 * - appendInput - content will be appended to input element
 */
class Input extends LitElement {
  /**
   * Optional name of the input
   *
   * @type {?string}
   */
  @property({ type: String })
  accessor name = null;

  /**
   * Optional label of the input, rendered on top of it
   *
   * @type {string}
   */
  @property({ type: String })
  accessor label = '';

  /**
   * Optional placeholder of the input
   *
   * @type {?string}
   */
  @property({ type: String })
  accessor placeholder = null;

  /**
   * Type of the input
   *
   * @type {("hidden" | "text" | "search" | "tel" | "url" | "email" | "password" | "datetime" | "date" | "month" | "week" | "time" | "datetime-local" | "number" | "range" | "color" | "checkbox" | "radio" | "file" | "submit" | "image" | "reset" | "button")}
   */
  @property({ type: String })
  accessor type = 'text';

  /**
   * @type {?string}
   */
  @property({ type: String, attribute: 'max-length' })
  accessor maxLength = null;

  /**
   * @type {?string}
   */
  @property({ type: String, attribute: 'input-id' })
  accessor inputId = null;

  /**
   * @type {string}
   */
  @property({ type: String })
  accessor value = '';

  /**
   * @type {?string}
   */
  @property({ type: String })
  accessor min = null;

  /**
   * @type {?string}
   */
  @property({ type: String })
  accessor max = null;

  /**
   * Whether input should be checked, used for radio
   *
   * @type {boolean}
   */
  @property({ type: Boolean })
  accessor checked = false;

  /**
   * Specifies if the input element is disabled
   *
   * @type {boolean}
   */
  @property({ type: Boolean, attribute: 'is-disabled' })
  accessor isDisabled = false;

  /**
   * Whether customers input already passed validation
   *
   * @type {boolean}
   */
  @property({ type: Boolean, attribute: 'is-valid' })
  accessor isValid = false;

  /**
   * Whether the customer input was determined as invalid, use only in the absence of `errorMessage`
   *
   * @type {boolean}
   */
  @property({ type: Boolean, attribute: 'is-invalid' })
  accessor isInvalid = false;

  /**
   * Whether the `form-field` bottom margin should be overridden
   *
   * @type {boolean}
   */
  @property({ type: Boolean, attribute: 'is-margin-disabled' })
  accessor isMarginDisabled = false;

  /**
   * Message shown under input if some error occurred
   *
   * @type {string}
   */
  @property({ type: String, attribute: 'error-message' })
  accessor errorMessage = '';

  /**
   * Provide instructions on how to fill a form field. Unlike a placeholder, hint stays visible even while the user is typing
   *
   * @type {string}
   */
  @property({ type: String })
  accessor hint = '';

  /**
   * Whether to turn off max-width CSS property of the input
   *
   * @type {boolean}
   */
  @property({ type: Boolean, attribute: 'is-full-width' })
  accessor isFullWidth = false;

  /**
   * Optional ID for automated testing
   *
   * @type {?string}
   */
  @property({ type: String, attribute: 'test-id' })
  accessor testId = null;

  static get styles() {
    return [style];
  }

  /**
   * @returns {boolean}
   */
  get isRadioButton() {
    return this.type === 'radio';
  }

  /**
   * @returns {boolean}
   */
  get isCheckbox() {
    return this.type === 'checkbox';
  }

  /**
   * @returns {boolean}
   */
  get hasError() {
    return (
      this.errorMessage !== '' && this.errorMessage !== null && this.errorMessage !== undefined
    );
  }

  /**
   * @returns {boolean}
   */
  get hasHint() {
    return this.hint !== '';
  }

  /**
   * Lifecycle function.
   * Called after the element’s DOM has been updated the first time, immediately before `updated` is called.
   */
  firstUpdated() {
    if (this.isCheckbox) {
      this.checked = this.value === 'true';
    }
    this.hideLabel();
  }

  /**
   * Hides label element if label is not provided by slot or attribute.
   * Slot element needs to be rendered to be able to detect if is empty.
   */
  hideLabel() {
    const labelSlot = this.shadowRoot.querySelector('slot[name="label"]');
    const labelEl = this.shadowRoot.querySelector('#label');

    if (labelSlot && !labelSlot.assignedElements().length && !this.label) {
      labelEl.style.display = 'none';
    }
  }

  /**
   *
   * @param {boolean} withDebounce
   * @returns {function}
   */
  @bound
  getInputChangeHandler(withDebounce = false) {
    return withDebounce ? debounce(this.handleInputChange, 300) : this.handleInputChange;
  }

  /**
   * input event handler
   *
   * Dispatch new value of the input to parent element
   */
  @bound
  handleInputChange() {
    if (this.isDisabled) {
      return; // If someone removes `isDisabled` property from the input in console, 'input-change' still won't fire
    }

    const el = this.shadowRoot.getElementById(this.inputId);
    this.value = this.isCheckbox ? el.checked : el.value;

    this.dispatchEvent(
      new CustomEvent('input-change', {
        bubbles: true,
        composed: false,
        detail: {
          value: this.value,
        },
      }),
    );
  }

  /**
   * @returns {TemplateResult}
   */
  renderLabel() {
    return html`
      <label id="label" class="label form-field__label" for=${this.inputId}>
        ${this.label ? this.label : html`<slot name="label"></slot>`}
      </label>
    `;
  }

  /**
   * @returns {?TemplateResult}
   */
  renderHint() {
    if (!this.hasHint) {
      return null;
    }
    return html` <p class="hint form-field__hint" id="${this.inputId}-hint">${this.hint}</p> `;
  }

  /**
   * @returns {?TemplateResult}
   */
  renderError() {
    if (!this.hasError) {
      return null;
    }
    return html` <osk-message type="error" .value=${this.errorMessage}></osk-message> `;
  }

  render() {
    if (this.isCheckbox || this.isRadioButton) {
      return html`
        <div class="form-field form-field--radiocheck ${this.isMarginDisabled ? 'mb-none' : ''}">
          <div class="form-field__control">
            <input
              type=${this.type}
              class=${classMap({
                radio: this.isRadioButton,
                checkbox: this.isCheckbox,
                'is-invalid': this.hasError || this.isInvalid,
              })}
              id=${this.inputId}
              name=${ifDefined(this.name)}
              .value=${this.value}
              .checked=${this.checked}
              ?disabled=${this.isDisabled}
              @input=${this.getInputChangeHandler()}
              aria-describedby=${ifDefined(this.hasHint ? `${this.inputId}-hint` : null)}
              data-test-id=${ifDefined(this.testId)}
            />
            <div
              class=${classMap({
                'radio-display': this.isRadioButton,
                'checkbox-display': this.isCheckbox,
              })}
            ></div>
          </div>
          <div class="form-field__description">
            ${this.renderLabel()} ${this.renderHint()} ${this.renderError()}
          </div>
        </div>
      `;
    }

    return html`
      <div class="form-field ${this.isMarginDisabled ? 'mb-none' : ''}">
        <div id="label" class="form-field__description">
          ${this.renderLabel()} ${this.renderHint()}
        </div>
        <div class="control-group form-field__control">
          <slot name="prependInput"></slot>
          <input
            type=${this.type}
            .value=${this.value}
            ?disabled=${this.isDisabled}
            maxlength=${ifDefined(this.maxLength)}
            min=${ifDefined(this.min)}
            max=${ifDefined(this.max)}
            class="text-input ${classMap({
              'is-invalid': this.hasError || this.isInvalid,
              'is-valid': this.isValid,
              'text-fullwidth': this.isFullWidth,
            })}"
            id=${this.inputId}
            name=${ifDefined(this.name)}
            placeholder=${ifDefined(this.placeholder)}
            @input=${this.getInputChangeHandler(true)}
            aria-describedby=${ifDefined(this.hasHint ? `${this.inputId}-hint` : null)}
            data-test-id=${ifDefined(this.testId)}
          />
          <slot name="appendInput"></slot>
        </div>
        ${this.renderError()}
      </div>
    `;
  }
}

if (!customElements.get('osk-input')) {
  customElements.define('osk-input', Input);
}

export default Input;
